"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { PointsBar } from "@/components/gamification/points-bar";
import { ConfirmExitModal } from "@/components/ui/confirm-exit-modal";
import { getSessionExitCopy } from "@/content/session-exit-copy";
import { cn } from "@/utils/cn";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { BRAND_LOGO_PATH } from "@/lib/brand-assets";
import { createClient } from "@/lib/supabase/client";
import { usePathname, useRouter } from "next/navigation";
import { LogOut } from "lucide-react";
import { i18n, localeLabels, Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

type NavbarProgress = {
    level: number;
    totalPoints: number;
    pointsIntoCurrentLevel: number;
    pointsSpanToNextLevel: number;
    pointsToNextLevel: number | null;
};

const navbarLabels = defineLocaleRecord<{ courses: string; login: string; signup: string; hub: string }>({
    en: { courses: "Courses", login: "Log in", signup: "Get Started", hub: "Hub" },
    fr: { courses: "Parcours", login: "Connexion", signup: "Commencer", hub: "Hub" },
    ru: { courses: "Курсы", login: "Войти", signup: "Начать", hub: "Хаб" },
    uk: { courses: "Курси", login: "Увійти", signup: "Почати", hub: "Хаб" },
    de: { courses: "Kurse", login: "Anmelden", signup: "Starten", hub: "Hub" },
    es: { courses: "Cursos", login: "Entrar", signup: "Empezar", hub: "Hub" },
    it: { courses: "Corsi", login: "Accedi", signup: "Inizia", hub: "Hub" },
    pt: { courses: "Cursos", login: "Entrar", signup: "Começar", hub: "Hub" },
});

const languageLabels = defineLocaleRecord<string>({
    en: "English",
    fr: "Français",
    ru: "Русский",
    uk: "Українська",
    de: "Deutsch",
    es: "Español",
    it: "Italiano",
    pt: "Português",
});

const sloganLabels = defineLocaleRecord<string>({
    en: "From Idea to Product. Step by Step.",
    fr: "De l'idee au produit. Etape par etape.",
    ru: "От идеи к продукту. Шаг за шагом.",
    uk: "Від ідеї до продукту. Крок за кроком.",
    de: "Von der Idee zum Produkt. Schritt fuer Schritt.",
    es: "De la idea al producto. Paso a paso.",
    it: "Dall'idea al prodotto. Passo dopo passo.",
    pt: "Da ideia ao produto. Passo a passo.",
});

export function Navbar({
    user,
    progress,
    enabledLocales,
    showProgressFeature = true,
}: {
    user: User | null;
    progress?: NavbarProgress | null;
    enabledLocales: readonly Locale[];
    showProgressFeature?: boolean;
}) {
    const [scrolled, setScrolled] = useState(false);
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const supabase = createClient();
    const router = useRouter();
    const pathname = usePathname();
    const maybeLocale = pathname?.split("/")[1];
    const enabledLocaleSet = new Set(enabledLocales);
    const fallbackLocale = enabledLocales.includes(i18n.defaultLocale) ? i18n.defaultLocale : enabledLocales[0] ?? i18n.defaultLocale;
    const locale =
        maybeLocale && enabledLocaleSet.has(maybeLocale as Locale) ? (maybeLocale as Locale) : fallbackLocale;
    const isHubPage = pathname?.startsWith(`/${locale}/hub`) ?? false;
    const labels = navbarLabels[locale];
    const slogan = sloganLabels[locale];
    const logoPath = BRAND_LOGO_PATH.split("?")[0] || "/logo.png";
    const homePath = `/${locale}`;
    const coursesPath = `/${locale}/courses`;
    const loginPath = `/${locale}/login`;
    const onboardingPath = `/${locale}/onboarding`;
    const hubPath = `/${locale}/hub`;
    const isPublicCoursesActive =
        pathname?.startsWith(coursesPath) ||
        pathname?.startsWith(`/${locale}/lessons`) ||
        pathname?.startsWith(`/${locale}/quiz`) ||
        false;
    const matchesCoursesMode =
        pathname?.startsWith(coursesPath) ||
        pathname?.startsWith(`/${locale}/lessons`) ||
        pathname?.startsWith(`/${locale}/quiz`) ||
        false;
    const isLearningMode = pathname ? /\/lessons\/[^/]+\/?$/.test(pathname) : false;
    const isHubMode =
        pathname?.startsWith(hubPath) ||
        pathname?.startsWith(`/${locale}/progress`) ||
        pathname?.startsWith(`/${locale}/certificates`) ||
        pathname?.startsWith(`/${locale}/billing`) ||
        pathname?.startsWith(`/${locale}/settings`) ||
        pathname?.startsWith(`/${locale}/help`) ||
        pathname?.startsWith(`/${locale}/updates`) ||
        false;
    const isCoursesMode = matchesCoursesMode || !isHubMode;
    const sessionExitCopy = getSessionExitCopy(locale);
    const switchLocale = (target: Locale) => {
        if (!pathname) {
            router.push(`/${target}`);
            return;
        }

        if (pathname === "/") {
            router.push(`/${target}`);
            return;
        }

        const segments = pathname.split("/");
        const currentFirst = segments[1];

        if (i18n.locales.includes(currentFirst as Locale)) {
            segments[1] = target;
            router.push(segments.join("/") || `/${target}`);
            return;
        }

        router.push(`/${target}`);
    };

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleSignOut = async () => {
        try {
            setIsSigningOut(true);
            await supabase.auth.signOut();
            setShowSignOutConfirm(false);
            router.push(homePath);
            router.refresh();
        } finally {
            setIsSigningOut(false);
        }
    };

    if (isLearningMode) {
        return null;
    }

    return (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-2 py-3 sm:px-4 sm:py-4">
            <header className={cn(
                "w-full max-w-6xl rounded-2xl border border-[#d5c7ad] bg-[#fffaf1]/90 backdrop-blur-xl transition-all duration-300",
                scrolled ? "py-2.5 shadow-lg shadow-[rgba(68,88,80,0.18)] sm:py-3" : "py-3 sm:py-4"
            )}>
                <div className="container flex items-center justify-between gap-2 px-3 sm:px-6">
                    <Link href={homePath} className="group flex items-center gap-3">
                        <Image
                            src={logoPath}
                            alt="Zipvibe"
                            width={44}
                            height={44}
                            priority
                            sizes="44px"
                            className="h-9 w-9 rounded-md transition-opacity group-hover:opacity-90"
                        />
                        <span className="hidden max-w-[15rem] text-[11px] font-bold leading-tight text-[#176454] transition-colors group-hover:text-[#176454] lg:block">
                            {slogan}
                        </span>
                    </Link>

                    {user && progress && showProgressFeature ? (
                        <div className={cn(
                            "hidden md:flex items-center transition-all",
                            isHubPage ? "opacity-55 scale-95" : "opacity-90"
                        )}>
                            <PointsBar
                                level={progress.level}
                                totalPoints={progress.totalPoints}
                                pointsIntoCurrentLevel={progress.pointsIntoCurrentLevel}
                                pointsSpanToNextLevel={progress.pointsSpanToNextLevel}
                                pointsToNextLevel={progress.pointsToNextLevel}
                            />
                        </div>
                    ) : null}

                    <nav className="flex items-center gap-1 sm:gap-1.5 md:gap-4">
                        <label htmlFor="language-switcher" className="sr-only">
                            Language
                        </label>
                        <select
                            id="language-switcher"
                            value={locale}
                            onChange={(event) => switchLocale(event.target.value as Locale)}
                            className="h-10 min-w-[4.1rem] rounded-lg border border-[#ddd2bf] bg-[#fbf6ed] px-2 text-[11px] font-medium text-[#5f716b] outline-none transition-colors hover:border-[#ccb590] focus:border-[#0f766e] sm:min-w-[7.5rem] sm:px-3 sm:text-xs"
                            aria-label="Language"
                        >
                            {enabledLocales.map((code) => (
                                <option key={code} value={code}>
                                    {localeLabels[code] ?? languageLabels[code]}
                                </option>
                            ))}
                        </select>

                        {user ? (
                            <div className="flex items-center gap-1.5 pl-2 border-l border-[#d4c7af] sm:gap-3 sm:pl-4">
                                <div className="inline-flex min-h-11 items-center rounded-xl border border-[#d1c3ab] bg-[#f5ecdd] p-1">
                                    <Link
                                        href={coursesPath}
                                        prefetch={false}
                                        className={cn(
                                            "inline-flex min-h-11 min-w-[4rem] items-center justify-center rounded-lg px-2.5 text-[11px] font-medium transition-colors duration-150 active:scale-[0.99] sm:min-w-[6rem] sm:px-4 sm:text-sm",
                                            isCoursesMode ? "bg-white text-[#1f2d29] shadow-[0_1px_2px_rgba(15,24,20,0.08)]" : "text-[#536762] hover:text-[#1f2d29]"
                                        )}
                                    >
                                        {labels.courses}
                                    </Link>
                                    <Link
                                        href={hubPath}
                                        className={cn(
                                            "inline-flex min-h-11 min-w-[4rem] items-center justify-center rounded-lg px-2.5 text-[11px] font-medium transition-colors duration-150 active:scale-[0.99] sm:min-w-[6rem] sm:px-4 sm:text-sm",
                                            isHubMode ? "bg-white text-[#1f2d29] shadow-[0_1px_2px_rgba(15,24,20,0.08)]" : "text-[#536762] hover:text-[#1f2d29]"
                                        )}
                                    >
                                        {labels.hub}
                                    </Link>
                                </div>
                                <Button
                                    onClick={() => setShowSignOutConfirm(true)}
                                    variant="secondary"
                                    size="sm"
                                    className="border-[#d5c8b0] bg-[#f0e7d9] text-[#4a5c56] hover:bg-[#ebdfcd] hover:text-[#9f4f3f]"
                                >
                                    <LogOut size={16} />
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 pl-2 border-l border-[#d4c7af] sm:gap-3 sm:pl-4">
                                <Link
                                    href={coursesPath}
                                    prefetch={false}
                                    aria-current={isPublicCoursesActive ? "page" : undefined}
                                    className={cn(
                                        "order-1 inline-flex min-h-11 items-center rounded-lg px-2.5 text-xs font-medium transition-colors sm:px-3 sm:text-sm",
                                        isPublicCoursesActive
                                            ? "bg-[#f4ebdc] text-[#1f2d29]"
                                            : "text-[#4a5d57] hover:text-[#1d2623]"
                                    )}
                                >
                                    {labels.courses}
                                </Link>
                                <Link
                                    href={loginPath}
                                    className="order-3 hidden min-h-11 items-center px-1.5 text-xs font-medium text-[#6c7f78] transition-colors hover:text-[#364a44] min-[420px]:inline-flex sm:order-2 sm:px-3 sm:text-sm"
                                >
                                    {labels.login}
                                </Link>
                                <Link href={onboardingPath} className="order-2 sm:order-3">
                                    <Button size="sm" className="min-h-11 rounded-lg border border-[#0f766e]/30 bg-[#0f766e] px-3 text-white hover:bg-[#0e655e] sm:px-4">
                                        <span className="hidden sm:inline">{labels.signup}</span>
                                        <span className="sm:hidden">Start</span>
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </nav>
                </div>
            </header>

            <ConfirmExitModal
                isOpen={showSignOutConfirm}
                title={sessionExitCopy.logoutTitle}
                description={sessionExitCopy.logoutDescription}
                confirmLabel={sessionExitCopy.logoutConfirm}
                cancelLabel={sessionExitCopy.stay}
                onConfirm={handleSignOut}
                onCancel={() => setShowSignOutConfirm(false)}
                isBusy={isSigningOut}
            />
        </div>
    );
}
